import {useState} from 'react';
import {useNavigate} from 'react-router';
import TimezoneSelect from 'react-timezone-select';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with timezone support so we can convert the user-selected
// timezone (from the form picker) to a correct UTC ISO timestamp.
// Without this, dayjs() would silently use the browser's local timezone.
dayjs.extend(utc);
dayjs.extend(timezone);

import ConfirmModal from '../components/ConfirmModal';
import {useAuth} from '../hooks/AuthContext';
import * as AppointmentApi from '../api/AppointmentApi';
import PageShell from '../components/PageShell';
import './AppointmentAdd.css';
import {problemMessage} from '../api/client';
import ChoiceRow from '../components/ChoiceRow';
import useIdempotencyKeys from '../hooks/useIdempotencyKeys';

export default function AppointmentAdd() {
  const auth = useAuth();
  const navigate = useNavigate();
  const keys = useIdempotencyKeys();
  const [accountData] = useState(auth.user);
  const [confirmModalShow, setConfirmModalShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const hasPhone = Boolean(
    accountData &&
    accountData.phone &&
    accountData.phone !== 'null' &&
    String(accountData.phone).trim() !== '',
  );

  const [newAppointment, setNewAppointment] = useState({
    date: dayjs().format('YYYY-MM-DD'),
    time: '10:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    note: '',
    email_alert: false,
    sms_alert: false,
    alert_before: ['1 hour'],
  });

  const [errors, setErrors] = useState({});

  const handleChangeForm = (e) => {
    const {name, value} = e.target;
    setNewAppointment((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleTimezoneChange = (timezone) => {
    setNewAppointment((prevState) => ({
      ...prevState,
      timezone: timezone.value || timezone,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!newAppointment.date) newErrors.date = 'Choose a date';
    if (!newAppointment.time) newErrors.time = 'Choose a time';

    if (newAppointment.alert_before.length > 0 && !newAppointment.email_alert && !newAppointment.sms_alert) {
      newErrors.alerts = 'Choose at least one reminder method';
    }

    if (
      newAppointment.alert_before.length === 0 &&
      (newAppointment.email_alert || newAppointment.sms_alert)
    ) {
      newErrors.alerts = 'Choose at least one reminder timing (e.g. 1 hour before)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      setConfirmModalShow(true);
    }
  };

  const onClickAddAppointment = () => {
    if (submitting) return;
    setSubmitting(true);
    // Parse the date+time in the user-selected timezone (NOT the browser's
    // local timezone). This ensures alert_time offsets computed on the backend
    // (start_time - 1h / 1d / 1w) align with the patient's intended time.
    const appointmentDateIso = dayjs
      .tz(`${newAppointment.date}T${newAppointment.time}`, newAppointment.timezone)
      .toISOString();

    const appointmentData = {
      patientId: auth.user.id,
      doctorId: null,
      clinicDivision: null,
      appointment_date: appointmentDateIso,
      timezone: newAppointment.timezone,
      note: newAppointment.note,
      email_alert: newAppointment.email_alert,
      sms_alert: newAppointment.sms_alert,
      alert_before: newAppointment.alert_before,
    };

    keys
      .run('appointment', appointmentData, (idempotencyKey) =>
        AppointmentApi.createAppointment(appointmentData, {idempotencyKey}),
      )
      .then((response) => {
        if (response.status === 'pending') {
          alert('Appointment reminder request submitted!');
        } else if (response.status === 'confirmed') {
          alert('Appointment reminder confirmed!');
        } else if (response.auto_assigned) {
          alert(`Appointment reminder assigned to ${response.doctor_name}!`);
        } else {
          alert('Appointment reminder created!');
        }
        navigate('/appointments/');
      })
      .catch((err) => {
        setSubmitting(false);
        alert(problemMessage(err, 'Failed to save the appointment'));
      });
  };

  return (
    <PageShell title="New Appointment Reminder" showBackButton={true}>
      <form onSubmit={handleSubmit} className="appt-add-container">
        <div className="appt-form-card">
          <div className="appt-field">
            <label className="appt-field-label" htmlFor="appt-date">
              Date
            </label>
            <input
              id="appt-date"
              type="date"
              name="date"
              className="appt-input"
              value={newAppointment.date}
              onChange={handleChangeForm}
              min={dayjs().format('YYYY-MM-DD')}
            />
            {errors.date && <div style={{color: '#C43B71', fontSize: '12px'}}>{errors.date}</div>}
          </div>

          <div className="appt-field">
            <label className="appt-field-label" htmlFor="appt-time">
              Time
            </label>
            <input
              id="appt-time"
              type="time"
              name="time"
              className="appt-input"
              value={newAppointment.time}
              onChange={handleChangeForm}
            />
            {errors.time && <div style={{color: '#C43B71', fontSize: '12px'}}>{errors.time}</div>}
          </div>

          <div className="appt-field">
            <label className="appt-field-label" id="appt-timezone-label">
              Timezone
            </label>
            <div style={{borderRadius: '12px'}}>
              <TimezoneSelect
                aria-labelledby="appt-timezone-label"
                value={newAppointment.timezone}
                onChange={handleTimezoneChange}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '48px',
                    borderRadius: '12px',
                    borderColor: state.isFocused ? '#c43b71' : '#E5E7EB',
                    backgroundColor: '#F9FAFB',
                    fontFamily: 'Inter',
                    fontSize: '15px',
                    boxShadow: state.isFocused ? '0 0 0 3px rgba(196, 59, 113, 0.35)' : 'none',
                    '&:hover': {
                      borderColor: state.isFocused ? '#c43b71' : '#E5E7EB',
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    zIndex: 100,
                  }),
                }}
              />
            </div>
          </div>

          <div className="appt-field">
            <label className="appt-field-label" htmlFor="appt-note">
              Note for yourself
            </label>
            <p className="appt-field-hint">Optional. Private notes only you can see.</p>
            <textarea
              id="appt-note"
              className="appt-textarea"
              placeholder="Add details like insurance info or special instructions…"
              name="note"
              value={newAppointment.note}
              onChange={handleChangeForm}
            />
          </div>

          <fieldset className="choice-group-fieldset">
            <legend className="choice-group-legend">Alert methods</legend>
            {errors.alerts && (
              <div style={{color: '#C43B71', fontSize: '12px', marginBottom: '8px'}}>{errors.alerts}</div>
            )}

            <ChoiceRow
              checked={newAppointment.email_alert}
              onToggle={() => setNewAppointment((prev) => ({...prev, email_alert: !prev.email_alert}))}
              label="Email"
            />
            <ChoiceRow
              checked={newAppointment.sms_alert}
              disabled={!hasPhone}
              onToggle={() => setNewAppointment((prev) => ({...prev, sms_alert: !prev.sms_alert}))}
              label="SMS (US Only)"
              description={hasPhone ? null : 'Add a phone number in your profile to turn on SMS'}
            />
          </fieldset>

          <fieldset className="choice-group-fieldset">
            <legend className="choice-group-legend">Alert before appointment</legend>

            {['1 hour', '1 day', '1 week'].map((time) => {
              const isChecked = newAppointment.alert_before.includes(time);
              return (
                <ChoiceRow
                  key={time}
                  checked={isChecked}
                  label={time}
                  onToggle={() =>
                    setNewAppointment((prev) => ({
                      ...prev,
                      alert_before: isChecked
                        ? prev.alert_before.filter((v) => v !== time)
                        : [...prev.alert_before, time],
                    }))
                  }
                />
              );
            })}
          </fieldset>

          <div style={{display: 'flex', gap: '12px', marginTop: '6px'}}>
            <button type="submit" className="primary-btn" style={{flex: 1}} disabled={submitting}>
              {submitting ? 'Saving…' : 'Add reminder'}
            </button>
            <button
              type="button"
              className="primary-btn"
              style={{flex: 1, background: '#6B7280', color: 'white'}}
              onClick={() => navigate('/appointments/')}
            >
              Cancel
            </button>
          </div>
        </div>

        <ConfirmModal
          show={confirmModalShow}
          onHide={() => setConfirmModalShow(false)}
          modalTitle="Confirm this reminder"
          btnText="Confirm"
          clickBtn={() => {
            setConfirmModalShow(false);
            onClickAddAppointment();
          }}
        >
          <div style={{fontFamily: 'var(--font-body), Manrope, system-ui', fontSize: 14, color: '#1A1A2E'}}>
            <p>We&apos;ll send your reminder to:</p>
            {newAppointment.email_alert && (
              <div
                style={{
                  padding: '12px 14px',
                  background: 'rgba(196,59,113,0.04)',
                  borderRadius: 10,
                  fontSize: 13,
                  marginBottom: 8,
                }}
              >
                <strong>Email:</strong> {accountData?.email || 'Your Email'}
              </div>
            )}
            {newAppointment.sms_alert && (
              <div
                style={{
                  padding: '12px 14px',
                  background: 'rgba(196,59,113,0.04)',
                  borderRadius: 10,
                  fontSize: 13,
                  marginBottom: 8,
                }}
              >
                <strong>SMS:</strong> {accountData?.phone}
              </div>
            )}
            <p style={{margin: '12px 0 0', color: '#6B7280'}}>
              Reminders will arrive{' '}
              {newAppointment.alert_before && newAppointment.alert_before.length > 0
                ? newAppointment.alert_before.join(', ')
                : '1 hour'}{' '}
              before your appointment.
            </p>
          </div>
        </ConfirmModal>
      </form>
    </PageShell>
  );
}
